import { useMutation, useQuery } from '@tanstack/react-query';
import { fetchData } from '../utils/utils.jsx';

/**
 * Hook for fetching data using react-query and axios
 * @param {Object} parameters - Holds the event name for the lambda, along with some other parameters
 * @param {string} url - URL address of the function
 * @param {string} [type="POST"] - HTTP request method
 * @param {number} refresh - Number, in seconds, for setting the data-refresh interval
 * @param {boolean} enable - Whether to enable the query or not
 * @param {any} placeholderData - Improve UX by showing some data before the query returns
 * @returns {Object} The result from the query
 * @returns {any} [return.data] - Data returned from the API call
 * @returns {boolean} [return.isLoading] - Loading indicator
 * @returns {Error|null} [return.error] - Error object, if any
*/
export const useFetch = ({ 
    parameters, 
    url, 
    type = "POST", 
    refresh = 2, 
    enable = true,
    placeholderData = []
}) => {
    const query = useQuery({ 
        queryKey: [parameters.event, parameters],
        queryFn:  () => fetchData(parameters, url, type),
        refetchInterval: 1000 * 60 * refresh,
        staleTime: 1000 * 60, // every 1 min,
        enabled: enable,
        placeholderData
    });

    return query;
}

/**
 * Hook for creating/updating/deleting data using react-query and axios
 * @param {Object} parameters - Holds the event name for the lambda, its URL, and some other parameters
 * @returns {Object} [return.mutation] - object that holds logic for the mutation
 */
export const useSubmit = () => {
    const mutation = useMutation({
        mutationFn: parameters => fetchData(parameters.body, parameters.url, "post")
    });

    return mutation;
}